import { filenameFromContentDisposition } from '@/lib/download-blob'
import {
  isRecord,
  json,
  nullableNumber,
  nullableString,
  request,
  requestNoContent,
  requireBoolean,
  requireString,
  send,
} from '@/lib/api'
import type { components } from '@/lib/openapi.generated'

import { reportPreviewApi } from '@/modules/report-table'

export { decodeReportRun, type ReportPagination, type ReportRunColumn, type ReportRunResult } from '@/modules/report-table'

import {
  type ReportConditionGroup,
  type ReportConditionNode,
  type ReportHavingGroup,
  type ReportHavingNode,
  type ReportEnvelope,
  type ReportQueryDefinition,
  type ReportSourceRef,
} from '../lib/report-definition'

type Schemas = components['schemas']

export type ReportPermission = 'read' | 'write' | 'delete' | 'manage_access'
export type ReportKind = 'structured' | 'sql'

export type ReportSummary = {
  reportDefinitionId: string
  entityId: string
  name: string
  description: string
  kind: ReportKind
  createdByUserId: string | null
  updatedAt: string
  canDelete: boolean
}

export type ReportDetail = ReportSummary & {
  queryDefinition: ReportQueryDefinition | null
  rawQuery: string | null
  updatedByUserId: string | null
  createdAt: string
  effectivePermissions: ReportPermission[]
}




export type ReportAccessLevel = 'read' | 'write' | 'full'
export type ReportGranteeKind = 'user' | 'group'

export type ReportGrantee = { kind: ReportGranteeKind; id: string; displayName: string | null; accessLevel: ReportAccessLevel }
export type ShareCandidate = { kind: ReportGranteeKind; id: string; name: string; email: string | null }
export type ReportGrantChange = { id: string; accessLevel: ReportAccessLevel | null }

export type ReportSubscriptionCadence = 'daily' | 'weekly' | 'monthly'
export type ReportSubscriptionRecipientKind = 'user' | 'group' | 'email'

export type ReportSubscriptionRecipient = Omit<Schemas['ReportSubscriptionRecipientResponse'], 'kind'> & {
  kind: ReportSubscriptionRecipientKind
}
export type ReportSubscriptionRecipientBody = Partial<Schemas['ReportSubscriptionRecipientRequest']>

export type ReportSubscription = {
  reportSubscriptionId: string
  reportDefinitionId: string
  recipients: ReportSubscriptionRecipient[]
  cadence: ReportSubscriptionCadence
  timeZone: string
  timeOfDayLocal: string
  dayOfWeek: number | null
  dayOfMonth: number | null
  enabled: boolean
  createdAt: string
  updatedAt: string
  failing: boolean
  lastError: string | null
}

export type ReportSubscriptionBody = {
  recipients: ReportSubscriptionRecipientBody[]
  cadence: ReportSubscriptionCadence
  timeZone: string
  timeOfDayLocal: string
  dayOfWeek?: number
  dayOfMonth?: number
  enabled?: boolean
}

export type SaveReportBody = {
  name: string
  description: string
  definition: string
}

function decodeKind(raw: unknown, path: string): ReportKind {
  const kind = requireString(raw, path)
  if (kind === 'structured' || kind === 'sql') return kind
  throw new Error(`Unexpected report kind "${kind}" at ${path}`)
}

export function decodePermission(raw: unknown, path: string): ReportPermission {
  const permission = requireString(raw, path)
  if (permission === 'read' || permission === 'write' || permission === 'delete' || permission === 'manage_access') return permission
  throw new Error(`Unexpected report permission "${permission}" at ${path}`)
}

export function decodeAccessLevel(raw: unknown, path: string): ReportAccessLevel {
  const level = requireString(raw, path)
  if (level === 'read' || level === 'write' || level === 'full') return level
  throw new Error(`Unexpected report access level "${level}" at ${path}`)
}

export function decodeGranteeKind(raw: unknown, path: string): ReportGranteeKind {
  const kind = requireString(raw, path)
  if (kind === 'user' || kind === 'group') return kind
  throw new Error(`Unexpected grantee kind "${kind}" at ${path}`)
}

export function parseJsonColumn<T>(raw: unknown, path: string): T | null {
  if (raw === null || raw === undefined) return null
  if (typeof raw === 'object') return raw as T
  const text = requireString(raw, path)
  try {
    return JSON.parse(text) as T
  } catch {
    throw new Error(`Malformed JSON at ${path}`)
  }
}

function decodeSummary(raw: unknown, path: string): ReportSummary {
  if (!isRecord(raw)) throw new Error(`Malformed report at ${path}`)
  return {
    reportDefinitionId: requireString(raw.reportDefinitionId, `${path}.reportDefinitionId`),
    entityId: requireString(raw.entityId, `${path}.entityId`),
    name: requireString(raw.name, `${path}.name`),
    description: requireString(raw.description, `${path}.description`),
    kind: decodeKind(raw.kind, `${path}.kind`),
    createdByUserId: nullableString(raw.createdByUserId, `${path}.createdByUserId`),
    updatedAt: requireString(raw.updatedAt, `${path}.updatedAt`),
    canDelete: requireBoolean(raw.canDelete, `${path}.canDelete`),
  } satisfies ReportSummary
}

function asGroup<Node extends object, Group extends Node>(node: Node | undefined): Group | undefined {
  if (node === undefined || 'and' in node || 'or' in node) return node as Group | undefined
  return { and: [node] } as unknown as Group
}

export function decodeReportDetail(raw: unknown): ReportDetail {
  if (!isRecord(raw)) throw new Error('Malformed report response')
  if (!Array.isArray(raw.effectivePermissions)) throw new Error('Expected an array for "effectivePermissions"')
  const envelope = parseJsonColumn<ReportEnvelope>(raw.definition, 'report.definition')
  if (!envelope || envelope.version !== 3) throw new Error('Unsupported report definition')
  const structured = envelope.kind === 'structured'
    ? {
        ...envelope.query,
        filter: asGroup<ReportConditionNode, ReportConditionGroup>(envelope.query.filter),
        having: asGroup<ReportHavingNode, ReportHavingGroup>(envelope.query.having),
        joins: envelope.query.joins?.map((join) => ({ ...join, on: asGroup<ReportConditionNode, ReportConditionGroup>(join.on) ?? { and: [] } })),
      }
    : null
  return {
    ...decodeSummary(raw, 'report'),
    queryDefinition: structured,
    rawQuery: envelope.kind === 'sql' ? envelope.query.text : null,
    updatedByUserId: nullableString(raw.updatedByUserId, 'report.updatedByUserId'),
    createdAt: requireString(raw.createdAt, 'report.createdAt'),
    effectivePermissions: raw.effectivePermissions.map((permission, index) => decodePermission(permission, `report.effectivePermissions.${index}`)),
  } satisfies ReportDetail
}



function decodeCadence(raw: unknown, path: string): ReportSubscriptionCadence {
  const cadence = requireString(raw, path)
  if (cadence === 'daily' || cadence === 'weekly' || cadence === 'monthly') return cadence
  throw new Error(`Unexpected subscription cadence "${cadence}" at ${path}`)
}

function decodeRecipientKind(raw: unknown, path: string): ReportSubscriptionRecipientKind {
  const kind = requireString(raw, path)
  if (kind === 'user' || kind === 'group' || kind === 'email') return kind
  throw new Error(`Unexpected subscription recipient kind "${kind}" at ${path}`)
}

function decodeRecipient(raw: unknown, path: string): ReportSubscriptionRecipient {
  if (!isRecord(raw)) throw new Error(`Malformed subscription recipient at ${path}`)
  return {
    kind: decodeRecipientKind(raw.kind, `${path}.kind`),
    userId: nullableString(raw.userId, `${path}.userId`),
    userGroupId: nullableString(raw.userGroupId, `${path}.userGroupId`),
    email: nullableString(raw.email, `${path}.email`),
    displayName: nullableString(raw.displayName, `${path}.displayName`),
  } satisfies ReportSubscriptionRecipient
}

function decodeSubscription(raw: unknown, path: string): ReportSubscription {
  if (!isRecord(raw)) throw new Error(`Malformed report subscription at ${path}`)
  if (!Array.isArray(raw.recipients)) throw new Error(`Expected an array for "${path}.recipients"`)
  return {
    reportSubscriptionId: requireString(raw.reportSubscriptionId, `${path}.reportSubscriptionId`),
    reportDefinitionId: requireString(raw.reportDefinitionId, `${path}.reportDefinitionId`),
    recipients: raw.recipients.map((recipient, index) => decodeRecipient(recipient, `${path}.recipients.${index}`)),
    cadence: decodeCadence(raw.cadence, `${path}.cadence`),
    timeZone: requireString(raw.timeZone, `${path}.timeZone`),
    timeOfDayLocal: requireString(raw.timeOfDayLocal, `${path}.timeOfDayLocal`),
    dayOfWeek: nullableNumber(raw.dayOfWeek, `${path}.dayOfWeek`),
    dayOfMonth: nullableNumber(raw.dayOfMonth, `${path}.dayOfMonth`),
    enabled: requireBoolean(raw.enabled, `${path}.enabled`),
    createdAt: requireString(raw.createdAt, `${path}.createdAt`),
    updatedAt: requireString(raw.updatedAt, `${path}.updatedAt`),
    failing: requireBoolean(raw.failing, `${path}.failing`),
    lastError: nullableString(raw.lastError, `${path}.lastError`),
  } satisfies ReportSubscription
}

export function decodeSubscriptionList(raw: unknown): ReportSubscription[] {
  if (!Array.isArray(raw)) throw new Error('Expected an array of report subscriptions')
  return raw.map((subscription, index) => decodeSubscription(subscription, `subscriptions.${index}`))
}

export function decodeGrantees(raw: unknown): ReportGrantee[] {
  if (!Array.isArray(raw)) throw new Error('Expected an array of report grantees')
  return raw.map((grantee, index) => {
    if (!isRecord(grantee)) throw new Error(`Malformed report grantee at grantees.${index}`)
    return {
      kind: decodeGranteeKind(grantee.kind, `grantees.${index}.kind`),
      id: requireString(grantee.id, `grantees.${index}.id`),
      displayName: nullableString(grantee.displayName, `grantees.${index}.displayName`),
      accessLevel: decodeAccessLevel(grantee.accessLevel, `grantees.${index}.accessLevel`),
    } satisfies ReportGrantee
  })
}

const base = (entityId: string | null) => entityId === null ? '/api/v1/reports' : `/api/v1/entities/${encodeURIComponent(entityId)}/reports`

export const reportingApi = {
  grantCandidates: (entityId: string, reportId: string, kind: ReportGranteeKind, search: string) =>
    request(`${base(entityId)}/${encodeURIComponent(reportId)}/grant-candidates?${new URLSearchParams({ search, kind })}`, (raw): ShareCandidate[] => {
      if (!Array.isArray(raw)) throw new Error('Expected an array of candidates')
      return raw.map((candidate, index) => {
        if (!isRecord(candidate)) throw new Error(`Malformed candidate at ${index}`)
        return {
          kind: decodeGranteeKind(candidate.kind, `candidates.${index}.kind`),
          id: requireString(candidate.id, `candidates.${index}.id`),
          name: requireString(candidate.name, `candidates.${index}.name`),
          email: nullableString(candidate.email, `candidates.${index}.email`),
        }
      })
    }),

  filterValues: (entityId: string | null, source: ReportSourceRef, field: string, search: string) =>
    request(`${base(entityId)}/filter-values`, (raw) => {
      if (!isRecord(raw) || !Array.isArray(raw.values)) throw new Error('Malformed filter values')
      return {
        values: raw.values.map((value, index) => requireString(value, `values.${index}`)),
        hasMore: requireBoolean(raw.hasMore, 'hasMore'),
      }
    }, json('POST', { source, field, search })),

  get: (entityId: string, reportId: string) =>
    request(`${base(entityId)}/${encodeURIComponent(reportId)}`, decodeReportDetail),

  preview: reportPreviewApi.preview,

  create: (entityId: string, body: SaveReportBody) =>
    request(base(entityId), decodeReportDetail, json('POST', body)),

  duplicate: (entityId: string, reportId: string) =>
    request(`${base(entityId)}/${encodeURIComponent(reportId)}/duplicate`, decodeReportDetail, json('POST', {})),

  update: (entityId: string, reportId: string, body: SaveReportBody) =>
    request(`${base(entityId)}/${encodeURIComponent(reportId)}`, decodeReportDetail, json('PUT', body)),

  remove: (entityId: string, reportId: string, force = false) =>
    requestNoContent(`${base(entityId)}/${encodeURIComponent(reportId)}${force ? '?force=true' : ''}`, { method: 'DELETE' }),

  grants: (entityId: string, reportId: string) =>
    request(`${base(entityId)}/${encodeURIComponent(reportId)}/grants`, decodeGrantees),

  setGrants: (entityId: string, reportId: string, users: ReportGrantChange[], groups: ReportGrantChange[]) =>
    requestNoContent(`${base(entityId)}/${encodeURIComponent(reportId)}/grants`, json('PUT', {
      users: users.map((change) => ({ userId: change.id, accessLevel: change.accessLevel })),
      groups: groups.map((change) => ({ groupId: change.id, accessLevel: change.accessLevel })),
    })),

  exportCsv: async (entityId: string, reportId: string) => {
    const response = await send(`${base(entityId)}/${encodeURIComponent(reportId)}/export`, {
      headers: { Accept: 'text/csv' },
    })
    return {
      blob: await response.blob(),
      filename: filenameFromContentDisposition(response.headers.get('Content-Disposition'), 'report.csv'),
    }
  },

  subscriptions: (entityId: string, reportId: string) =>
    request(`${base(entityId)}/${encodeURIComponent(reportId)}/subscriptions`, decodeSubscriptionList),

  createSubscription: (entityId: string, reportId: string, body: ReportSubscriptionBody) =>
    request(`${base(entityId)}/${encodeURIComponent(reportId)}/subscriptions`, (raw) => decodeSubscription(raw, 'subscription'), json('POST', body)),

  updateSubscription: (entityId: string, reportId: string, subscriptionId: string, body: ReportSubscriptionBody) =>
    request(`${base(entityId)}/${encodeURIComponent(reportId)}/subscriptions/${encodeURIComponent(subscriptionId)}`, (raw) => decodeSubscription(raw, 'subscription'), json('PUT', body)),

  revokeSubscription: (entityId: string, reportId: string, subscriptionId: string) =>
    requestNoContent(`${base(entityId)}/${encodeURIComponent(reportId)}/subscriptions/${encodeURIComponent(subscriptionId)}/revoke`, { method: 'POST' }),

  removeSubscription: (entityId: string, reportId: string, subscriptionId: string) =>
    requestNoContent(`${base(entityId)}/${encodeURIComponent(reportId)}/subscriptions/${encodeURIComponent(subscriptionId)}`, { method: 'DELETE' }),

  exportPreview: reportPreviewApi.exportPreview,

}
